"use client";

import { useRouter, usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import Button from "./Button";

interface HeaderClientProps {
  userRole?: {
    role: "admin" | "employee" | "customer";
    status: "active" | "inactive";
    userId: string;
    email: string;
  } | null;
}

export default function HeaderClient({ userRole }: HeaderClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoText, setShowLogoText] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // If not on homepage, always show the text
      if (pathname !== '/') {
        setShowLogoText(true);
        return;
      }
      
      // On homepage, check if we're scrolled past the hero section
      const heroSection = document.querySelector('section');
      if (heroSection) {
        const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
        const shouldShow = window.scrollY > heroBottom - 150;
        setShowLogoText(shouldShow);
      }
    };

    // Check initial scroll position on mount
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pathname]);

  const goToPortal = () => {
    if (userRole?.role === "employee" || userRole?.role === "admin") {
      router.push("/portal");
    } else if (userRole?.role === "customer") {
      router.push("/customer");
    } else {
      router.push("/signin");
    }
  };

  const navLinks = [
    { href: "/blog", label: "Blog" },
    { href: "/addons", label: "Addons" },
  ];

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between items-center h-16">
          {/* Desktop Logo - left side with expansion */}
          <Link href="/" className="hidden md:flex group items-center font-poppins text-2xl font-bold text-gray-900 hover:text-gray-700 transition-colors">
            <Image 
              src="/images/logos/Logo_Only_Black.svg" 
              alt="Allegory Logo" 
              width={32} 
              height={32}
              className="h-8 w-8"
            />
            {/* Text that appears on scroll */}
            <div className={`overflow-hidden transition-all duration-500 ${
              showLogoText ? 'max-w-32 ml-0 delay-300' : 'max-w-0 ml-0 delay-0'
            }`}>
              <span className={`font-poppins text-xl font-bold text-gray-900 whitespace-nowrap transition-opacity duration-500 ${
                showLogoText ? 'opacity-100 delay-300' : 'opacity-0 delay-0'
              }`}>
                LLEGORY
              </span>
            </div>
          </Link>
          
          {/* Mobile Logo - always centered */}
          <Link href="/" className="md:hidden flex group items-center font-poppins text-2xl font-bold text-gray-900 hover:text-gray-700 transition-colors absolute left-1/2 -translate-x-1/2">
            <Image 
              src="/images/logos/Logo_Only_Black.svg" 
              alt="Allegory Logo" 
              width={32} 
              height={32}
              className="h-8 w-8"
            />
            {/* Text that appears on scroll */}
            <div className={`overflow-hidden transition-all duration-500 ${
              showLogoText ? 'max-w-32 ml-0 delay-300' : 'max-w-0 ml-0 delay-0'
            }`}>
              <span className={`font-poppins text-xl font-bold text-gray-900 whitespace-nowrap transition-opacity duration-500 ${
                showLogoText ? 'opacity-100 delay-300' : 'opacity-0 delay-0'
              }`}>
                LLEGORY
              </span>
            </div>
          </Link>

          {/* Desktop Navigation and Buttons - right aligned */}
          <div className="hidden md:flex items-center gap-8">
            {/* Desktop Navigation - right aligned */}
            <nav className="flex space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`font-poppins text-sm transition-colors ${
                    pathname.includes(link.href)
                      ? "text-gray-900 font-semibold"
                      : "text-gray-600 hover:text-gray-900 border-b hover:border-gray-900 border-white transition-all duration-300"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Sign-in/portal button */}
            {userRole && (
              <Button onClick={goToPortal} variant="secondary" size="sm">
                {userRole.role === "customer" ? "Customer Portal" : "Portal"}
              </Button>
            )}
            {!userRole && (
              <Button onClick={() => router.push('/signin')} size="sm">
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile menu button and title */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-600 hover:text-gray-900 transition-colors p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 md:hidden bg-white border-t border-gray-200 shadow-lg z-40">
            <div className="px-4 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block font-poppins text-sm transition-colors ${
                    pathname === link.href
                      ? "text-gray-900 font-semibold"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-200">
                {userRole ? (
                  <Button onClick={() => { goToPortal(); setIsMobileMenuOpen(false); }} variant="secondary" size="sm" className="w-full">
                    {userRole.role === "customer" ? "Customer Portal" : "Portal"}
                  </Button>
                ) : (
                  <Button onClick={() => { router.push('/signin'); setIsMobileMenuOpen(false); }} size="sm" className="w-full">
                    Sign In
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}